import { Canvas, FabricObject } from 'fabric';

import { reviver } from './draw-revive';
import { CUSTOM_CANVAS_PROPS } from '../draw-canvas.constants';
import { LanguageCodes } from 'global/constants';

const maxHistory = 10;

type CanvasHistory = {
  version: string;
  objects: FabricObject[];
};

type HistoryCallbacks = {
  onChange: (historyPosition: number, historyLength: number) => void;
};

export class DrawHistory {
  private undoStack: CanvasHistory[] = [];
  private position = 1;

  private canvas: Canvas;
  private callbacks: HistoryCallbacks;
  private isCreateMode: boolean;

  constructor({
    canvas,
    callbacks,
    isCreateMode,
  }: {
    canvas: Canvas;
    callbacks: HistoryCallbacks;
    isCreateMode: boolean;
  }) {
    this.canvas = canvas;
    this.callbacks = callbacks;
    this.undoStack = [this.canvas.toObject(CUSTOM_CANVAS_PROPS)];
    this.isCreateMode = isCreateMode;
    this.listenForChanges();
  }

  saveCanvasState() {
    if (this.undoStack.length >= maxHistory) {
      // remove the oldest state if we exceed max history
      this.undoStack.shift();
    }
    const canvasState = this.canvas.toObject(CUSTOM_CANVAS_PROPS);
    this.undoStack.push(canvasState);
    this.position = this.undoStack.length;
    this.callbacks.onChange(this.position, this.undoStack.length);
  }

  listenForChanges() {
    this.canvas.on('object:added', () => {
      this.saveCanvasState();
    });
    this.canvas.on('object:modified', () => {
      this.saveCanvasState();
    });
    this.canvas.on('object:removed', () => {
      this.saveCanvasState();
    });
  }

  dontListenForChanges() {
    this.canvas.off('object:added');
    this.canvas.off('object:modified');
    this.canvas.off('object:removed');
  }

  undo(language: LanguageCodes) {
    if (this.canUndo()) {
      this.dontListenForChanges();

      this.position--;
      const previousState = this.undoStack[this.position - 1];
      this.loadHistory(previousState, language).finally(() => {
        this.callbacks.onChange(this.position, this.undoStack.length);
      });
    }
  }

  redo(language: LanguageCodes) {
    if (this.canRedo()) {
      this.dontListenForChanges();

      const nextState = this.undoStack[this.position];
      this.position++;
      this.loadHistory(nextState, language).finally(() => {
        this.callbacks.onChange(this.position, this.undoStack.length);
      });
    }
  }

  loadHistory(json: CanvasHistory, language: LanguageCodes) {
    const pendingPromises: Promise<void>[] = [];

    return this.canvas
      .loadFromJSON(json, (object, instance) =>
        reviver({
          canvas: this.canvas,
          serializedObject: object,
          instance,
          isCreateMode: this.isCreateMode,
          language,
          pendingPromises,
        })
      )
      .then(canvas => {
        canvas.requestRenderAll();
        this.listenForChanges();
      });
  }

  canUndo() {
    return this.position - 1 > 0;
  }

  canRedo() {
    return this.position < this.undoStack.length;
  }
}
